import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  DEFAULT_LIST_PAGE_SIZE,
  LIST_PAGE_SIZE_OPTIONS,
  clampPage,
  paginateSlice,
  type PageSlice,
} from '../../lib/listPagination';

/**
 * Shared pagination for the admin org lists (Referrers / Sponsors /
 * Employers). The backing endpoints return the full filtered set, so
 * paging is client-side over the fetched array; all page math lives in
 * src/lib/listPagination.ts and is shared by every list.
 *
 * Behaviour contract:
 * - Changing `resetKey` (search text, Active/Archived view, …) returns
 *   the list to page 1.
 * - If the set shrinks under the current page (a row is archived or
 *   removed, a refetch returns fewer rows), the page CLAMPS to the last
 *   valid page — the admin is never stranded on an empty page N.
 * - Changing the page size returns to page 1.
 */
export function useListPagination<T>(
  items: readonly T[],
  resetKey: unknown,
  initialPageSize: number = DEFAULT_LIST_PAGE_SIZE,
): PageSlice<T> & {
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
} {
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  // Filter/search changes restart paging from the top.
  useEffect(() => {
    setPageState(1);
  }, [resetKey]);

  const slice = useMemo(
    () => paginateSlice(items, page, pageSize),
    [items, page, pageSize],
  );

  // Keep the raw state in step with the clamped page so prev/next and
  // the jump box never act on a page the current set no longer has.
  useEffect(() => {
    if (slice.page !== page) setPageState(slice.page);
  }, [slice.page, page]);

  const setPage = (next: number) =>
    setPageState(clampPage(next, items.length, pageSize));

  const setPageSize = (size: number) => {
    setPageSizeState(size);
    setPageState(1);
  };

  return { ...slice, pageSize, setPage, setPageSize };
}

interface ListPaginationProps {
  page: number;
  pageCount: number;
  total: number;
  start: number;
  end: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Plural noun for the count line, e.g. "referrers". */
  itemLabel: string;
}

/**
 * The pager bar rendered under each list: truthful "Showing X–Y of Z"
 * counts, prev/next, a "Page N of M" indicator, a jump-to-page box, and
 * a page-size select. Native buttons/inputs/select keep it fully
 * keyboard-operable; the bar wraps so it fits phone-width containers.
 */
export function ListPagination({
  page,
  pageCount,
  total,
  start,
  end,
  pageSize,
  onPageChange,
  onPageSizeChange,
  itemLabel,
}: ListPaginationProps) {
  const [jumpValue, setJumpValue] = useState('');

  if (total === 0) return null;

  const submitJump = (e: React.FormEvent) => {
    e.preventDefault();
    const target = parseInt(jumpValue, 10);
    if (Number.isFinite(target)) onPageChange(target);
    setJumpValue('');
  };

  const navBtn =
    'flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-white cursor-pointer disabled:cursor-default focus:outline-none focus:ring-2 focus:ring-brand-primary/30';

  return (
    <nav
      aria-label={`${itemLabel} pagination`}
      className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
    >
      <p aria-live="polite" className="text-xs font-medium text-slate-500">
        Showing <span className="font-bold text-slate-800">{start}–{end}</span> of{' '}
        <span className="font-bold text-slate-800">{total}</span> {itemLabel}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          Rows per page
          <select
            value={pageSize}
            onChange={e => onPageSizeChange(parseInt(e.target.value, 10))}
            aria-label="Rows per page"
            className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
          >
            {LIST_PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        {pageCount > 1 && (
          <>
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              aria-label="Previous page"
              className={navBtn}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Prev</span>
            </button>
            <span className="px-1 text-xs font-bold text-slate-800">
              Page {page} of {pageCount}
            </span>
            <button
              type="button"
              disabled={page >= pageCount}
              onClick={() => onPageChange(page + 1)}
              aria-label="Next page"
              className={navBtn}
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="h-4 w-4" />
            </button>
            <form onSubmit={submitJump} className="flex items-center gap-1.5">
              <label htmlFor={`jump-${itemLabel}`} className="text-xs font-medium text-slate-500">
                Go to
              </label>
              <input
                id={`jump-${itemLabel}`}
                type="number"
                min={1}
                max={pageCount}
                value={jumpValue}
                onChange={e => setJumpValue(e.target.value)}
                placeholder={String(page)}
                aria-label={`Jump to page (1 to ${pageCount})`}
                className="w-16 px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
              />
              <button type="submit" className={navBtn}>
                Go
              </button>
            </form>
          </>
        )}
      </div>
    </nav>
  );
}
